import React, { useEffect, useState } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import styles from "./add-user-drawer-modal.module.scss";
import { Title } from "@src/components/atoms/title/title";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { RoleSelector } from "@src/components/molecules/roles-selector/roles-selector";
import { LanguageSelectorWithSearch } from "@src/components/molecules/language-selector-with-search/language-selector-with-search";
import { TimezoneSelectorWithSearch } from "@src/components/molecules/timezone-selector-with-search/timezone-selector-with-search";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";

import { DrawerButtonWrapper } from "@src/components/molecules/drawer-button-wrapper/drawer-button-wrapper";
import { useDispatch } from "react-redux";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { Button } from "@src/components/atoms/buttons/button/button";
import { ZitadelUser, ZitadelUserType } from "@src/types/user-management";
import {
  updateModalsCloseDrawerModal,
  updateUserListResultsReady,
} from "@src/reducers/your-reducer";
import { postCreateUserUseCase } from "@src/cloud-connect/user-manager/use-cases/post-create-user-use-case";
import { validateUserInputs } from "@src/cloud-connect/user-manager/utils/validate-user-drawer-inputs";
import { trimUserInputs } from "@src/cloud-connect/user-manager/utils/trim-user-drawer-inputs";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { useI18N } from "@src/language/language-context";
import { getValidUserRolesUseCase } from "@src/cloud-connect/user-manager/use-cases/get-valid-user-roles-use-case";
import Spinner from "@src/components/atoms/spinner/spinner";

const cssClassName = "add-user-drawer-modal";

export const AddUserDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const [creatingUser, setCreatingUser] = useState(false);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const texts = {
    addUserTitle: i18n("modules.addUserDrawerModal.addUserTitle"),
    userNameLabel: i18n("modules.addUserDrawerModal.userNameLabel"),
    userNamePlaceholder: i18n("modules.addUserDrawerModal.userNamePlaceholder"),
    givenNameLabel: i18n("modules.addUserDrawerModal.givenNameLabel"),
    givenNamePlaceholder: i18n(
      "modules.addUserDrawerModal.givenNamePlaceholder",
    ),
    familyNameLabel: i18n("modules.addUserDrawerModal.familyNameLabel"),
    familyNamePlaceholder: i18n(
      "modules.addUserDrawerModal.familyNamePlaceholder",
    ),
    emailLabel: i18n("modules.addUserDrawerModal.emailLabel"),
    emailPlaceholder: i18n("modules.addUserDrawerModal.emailPlaceholder"),
    createUserButton: i18n("modules.addUserDrawerModal.createUserButton"),
    cancelButton: i18n("modules.addUserDrawerModal.cancelButton"),
    rolesWarningText: i18n("modules.addUserDrawerModal.rolesWarningText"),
    inputsCheckWarningText: i18n(
      "modules.addUserDrawerModal.inputsCheckWarningText",
    ),
    userCreatedSuccessText: i18n(
      "modules.addUserDrawerModal.userCreatedSuccessText",
    ),
    userCreatedErrorText: i18n(
      "modules.addUserDrawerModal.userCreatedErrorText",
    ),
  };

  const [user, setUser] = useState<Partial<ZitadelUser> | null>({
    id: null,
    type: ZitadelUserType.HUMAN,
    email: "",
    username: "",
    given_name: "",
    family_name: "",
    nickname: null,
    language: "eng",
    chosen_timezone: "UTC",
    roles: [],
  });

  const closeDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addUserDrawerModal",
      }),
    );
  };

  const handleAddUser = async () => {
    setCreatingUser(true);

    if (user.roles.length === 0) {
      warningSnackbar(texts.rolesWarningText, 5000);
      setCreatingUser(false);
      return;
    }
    const trimmedUser = trimUserInputs(user);

    const { isValid, errors } = validateUserInputs(trimmedUser);

    if (!isValid) {
      warningSnackbar(
        `${texts.inputsCheckWarningText} ${errors.join(", ")}`,
        5000,
      );
      setCreatingUser(false);
      return;
    }

    const response = await postCreateUserUseCase(trimmedUser as ZitadelUser);

    const dateToTriggerReload = new Date().toISOString();

    if (response.success && response.data) {
      successSnackbar(texts.userCreatedSuccessText, 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
    } else {
      errorSnackbar(response.error || texts.userCreatedErrorText, 5000);
      console.error("Error creating user:", response.error);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
    }

    setCreatingUser(false);

    closeDrawer();
  };

  useEffect(() => {
    const loadValidUserRoles = async () => {
      setLoading(true);
      const roles = await getValidUserRolesUseCase();
      setRoleOptions(roles.data || []);
      setLoading(false);
    };

    loadValidUserRoles();
  }, []);

  return (
    <DrawerModal id={"addUserDrawerModal"}>
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={32}>
          <Title tag={"h2"} text={texts.addUserTitle} />
          {loading ? (
            <Spinner />
          ) : (
            <div className={styles[`${cssClassName}__content-wrapper`]}>
              <Input2025
                labelText={texts.userNameLabel}
                inputPlaceHolder={texts.userNamePlaceholder}
                inputValue={user.username || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, username: e.target.value })
                }
              />
              <Input2025
                labelText={texts.givenNameLabel}
                inputPlaceHolder={texts.givenNamePlaceholder}
                inputValue={user.given_name || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, given_name: e.target.value })
                }
              />
              <Input2025
                labelText={texts.familyNameLabel}
                inputPlaceHolder={texts.familyNamePlaceholder}
                inputValue={user.family_name || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, family_name: e.target.value })
                }
              />
              <RoleSelector
                user={user}
                setUser={setUser}
                roleOptions={roleOptions}
              />
              <Input2025
                labelText={texts.emailLabel}
                inputPlaceHolder={texts.emailPlaceholder}
                inputValue={user.email || ""}
                onChangeFunc={(e) =>
                  setUser({ ...user, email: e.target.value })
                }
              />
              <LanguageSelectorWithSearch
                selectedLanguage={user.language}
                setSelectedLanguage={(isoCode) =>
                  setUser({ ...user, language: isoCode })
                }
              />
              <TimezoneSelectorWithSearch
                selectedTimezone={user.chosen_timezone || "UTC"}
                setSelectedTimezone={(tz) =>
                  setUser({ ...user, chosen_timezone: tz })
                }
              />
              <DrawerButtonWrapper>
                <ButtonWithLoader
                  fun={handleAddUser}
                  text={texts.createUserButton}
                  variant={"full-width"}
                  loading={creatingUser}
                />
                <Button
                  fun={closeDrawer}
                  text={texts.cancelButton}
                  variant={["secondary", "full-width"]}
                />
              </DrawerButtonWrapper>
            </div>
          )}
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
};
